import { useState, useEffect, useCallback } from "react";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Download, Trash2, FileText, File as FileIcon, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ICON_BY_EXT = {
  pdf:  <FileText className="h-4 w-4 text-red-600" />,
  doc:  <FileText className="h-4 w-4 text-blue-700" />,
  docx: <FileText className="h-4 w-4 text-blue-700" />,
  xls:  <FileText className="h-4 w-4 text-emerald-700" />,
  xlsx: <FileText className="h-4 w-4 text-emerald-700" />,
  png:  <ImageIcon className="h-4 w-4 text-purple-600" />,
  jpg:  <ImageIcon className="h-4 w-4 text-purple-600" />,
  jpeg: <ImageIcon className="h-4 w-4 text-purple-600" />,
  webp: <ImageIcon className="h-4 w-4 text-purple-600" />,
  heic: <ImageIcon className="h-4 w-4 text-purple-600" />,
};

function iconFor(name = "") {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  return ICON_BY_EXT[ext] || <FileIcon className="h-4 w-4 text-[#7A8A84]" />;
}
function fmtSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * DocumentAttachments — drop-in panel for showing/uploading/downloading/deleting
 * files attached to a booking or a visit request.
 *
 * Props:
 *   entityType: "booking" | "request"
 *   entityId:   the entity's UUID
 *   compact:    when true, hides the "how it works" hint below the list
 */
export function DocumentAttachments({ entityType, entityId, compact = false }) {
  const { user, hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [docType, setDocType] = useState("");
  const canDownload = hasPerm("documents.download");
  const canManage = hasPerm("documents.manage");

  useEffect(() => {
    // Phase 63 — offer the org's required-document labels as upload tags so
    // license files get matched to the checklist automatically.
    api.get("/housing/medical-license-settings")
      .then(({ data }) => {
        if (data?.docs_feature_enabled) setDocTypes(data.required_docs || []);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/entity/${entityType}/${entityId}`);
      setItems(data || []);
    } catch (err) {
      // Silently ignore 403 (permission-scoped views) — the panel still
      // shows "No attachments yet." rather than spamming a toast on every
      // dialog open for users who can't view them anyway.
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Failed to load documents");
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 10 MB — skipped`);
        continue;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append("entity_type", entityType);
        form.append("entity_id", entityId);
        form.append("file", f);
        if (docType) form.append("requirement", docType);
        await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      } finally {
        setUploading(false);
      }
    }
    toast.success("Uploaded");
    load();
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const canDeleteRow = (doc) => canManage || doc.uploaded_by_user_id === user?.id;

  return (
    <div className="space-y-2" data-testid="document-attachments">
      <div className="flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-wider text-[#7A8A84] font-semibold flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5" /> Attachments {items.length > 0 && <span className="text-[#525C58]">({items.length})</span>}
        </h4>
        <div className="flex items-center gap-1.5">
          {docTypes.length > 0 && (
            <select value={docType} onChange={(e) => setDocType(e.target.value)}
                    className="text-[11px] border border-[#E8E8E6] rounded px-1.5 py-1 text-[#525C58] max-w-[150px] bg-white"
                    data-testid="document-type-select">
              <option value="">Document type…</option>
              {docTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <label className="cursor-pointer">
            <input type="file" multiple onChange={upload} className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic"
              data-testid="document-upload-input" disabled={uploading || !entityId} />
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border transition-colors
              ${uploading ? "bg-gray-100 text-gray-400 border-gray-200 cursor-wait"
                          : "bg-white text-[#1B4332] border-[#1B4332]/30 hover:bg-[#1B4332]/5 cursor-pointer"}`}>
              <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload"}
            </span>
          </label>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-[#7A8A84]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-[#7A8A84] italic">No attachments yet.</p>
      ) : (
        <div className="space-y-1">
          {items.map((doc) => (
            <div key={doc.id} className="flex items-center gap-2 rounded border border-[#E8E8E6] bg-white px-2 py-1.5 text-xs hover:border-[#1B4332]/40 transition"
                 data-testid={`document-row-${doc.id}`}>
              {iconFor(doc.filename)}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-[#1C1F1E]" title={doc.filename}>
                  {doc.filename}
                  {doc.requirement && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-[#F0F5F2] border border-[#1B4332]/15 px-1.5 py-px text-[9px] text-[#1B4332] align-middle"
                          data-testid={`document-requirement-tag-${doc.id}`}>
                      {doc.requirement}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#7A8A84]">
                  {fmtSize(doc.size)} · {doc.uploaded_by_name || doc.uploaded_by_role || "unknown"}
                  {doc.uploaded_at && ` · ${new Date(doc.uploaded_at).toLocaleDateString()}`}
                </div>
              </div>
              {(canDownload || doc.uploaded_by_user_id === user?.id) && (
                <button onClick={() => download(doc)} title="Download"
                        className="p-1 rounded hover:bg-[#F4F4F1] text-[#525C58]"
                        data-testid={`document-download-${doc.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </button>
              )}
              {canDeleteRow(doc) && (
                <button onClick={() => remove(doc)} title="Delete"
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        data-testid={`document-delete-${doc.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!compact && (
        <p className="text-[10px] text-[#7A8A84] italic">
          PDF · Word · Excel · images (10 MB max). Downloads require permission — ask your admin if the download icon is hidden.
        </p>
      )}
    </div>
  );
}
